import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import { Heart, Star, Gift, Music, Video, FileText, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import type { Band } from '../types/database';

interface FanAreaModalProps {
  band: Band;
  isOpen: boolean;
  onClose: () => void;
}

export function FanAreaModal({ band, isOpen, onClose }: FanAreaModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    if (!user || !profile) {
      setError('You must be logged in to subscribe');
      return;
    }

    if (!band.fan_area_price) {
      setError('Price not set for this fan area');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('Creating checkout session for band:', band.id, 'price:', band.fan_area_price);

      // Create Stripe checkout session
      const response = await supabase.functions.invoke('create-stripe-checkout-session', {
        body: {
          band_id: band.id,
          price_amount: Math.round(band.fan_area_price * 100), // Convert to cents
        },
      });

      console.log('Edge function response:', response);

      if (response.error) {
        console.error('Edge Function error:', response.error);
        throw new Error(response.error.message || 'Payment setup failed');
      }

      if (!response.data) {
        throw new Error('No response data received from payment service');
      }

      // Check if the response contains an error from the Edge Function
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received from payment service');
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to start subscription process');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
    }).format(price);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${band.name} Fan Zone`}>
      <div className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
            {error}
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-light mb-2">
            {t('fanArea.becomeFanOf').replace('{bandName}', band.name)}
          </h2>
          <p className="text-light/60">
            {t('fanArea.supportDescription').replace('{bandName}', band.name)}
          </p>
        </div>

        {/* Price */}
        <div className="text-center bg-gray-custom/10 rounded-lg p-6">
          <div className="text-3xl font-bold text-light mb-2">
            {band.fan_area_price ? formatPrice(band.fan_area_price) : t('fanArea.free')}
          </div>
          <div className="text-light/60">{t('fanArea.oneTime')}</div>
        </div>

        {/* Description */}
        {band.fan_area_description && (
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark mb-3">{t('fanArea.whatYouGet')}</h3>
            <div className="text-dark/80 whitespace-pre-line">
              {band.fan_area_description}
            </div>
          </div>
        )}

        {/* One-time payment description */}
        <div className="space-y-6">
          <div>
            <strong>{t('fanArea.howItWorks')}</strong>
          </div>
          <div className="text-light/60 space-y-2">
            <p>• {t('fanArea.howItWorksPoint1')}</p>
            <p>• {t('fanArea.howItWorksPoint2')}</p>
            <p>• {t('fanArea.howItWorksPoint3')}</p>
            <p>• {t('fanArea.howItWorksPoint4')}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 pt-4">
          <button
            onClick={onClose}
            className="btn-outline flex-1"
            disabled={loading}
          >
            {t('fanArea.maybeLater')}
          </button>
          <button
            onClick={handleSubscribe}
            className="btn-primary flex-1 flex items-center justify-center"
            disabled={loading || !user}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {t('fanArea.processing')}
              </>
            ) : (
              <>
                <Heart className="w-5 h-5 mr-2" />
                {band.fan_area_price ? t('fanArea.subscribeButton').replace('{price}', formatPrice(band.fan_area_price)) : t('fanArea.joinFree')}
              </>
            )}
          </button>
        </div>

        {!user && (
          <div className="text-center text-light/60 text-sm">
            {t('fanArea.loginRequired')}
          </div>
        )}

        {/* Security Notice */}
        <div className="text-xs text-light/40 text-center">
          {t('fanArea.securePayment')}
        </div>
      </div>
    </Modal>
  );
}